import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Download } from 'lucide-react';
import {
  formatCents,
  formatQty,
  type Paginated,
  type StockBalance,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { FilterBar } from '@/components/FilterBar';
import { SmartDataTable, type Column } from '@/components/SmartDataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { EmptyState, ErrorState, NoResultsState, TableSkeleton } from '@/components/states';
import { useStockLocations } from '@/lib/queries';
import { downloadCsv } from '@/lib/csv';

/** Uma linha de saldo é a dupla item+local; o id sintético dá chave à tabela. */
type BalanceRow = StockBalance & { id: string };

/**
 * Saldos por item e local — a pergunta "quanto tem, e onde".
 *
 * Linhas zeradas ficam escondidas por padrão: o almoxarife quer ver o que tem,
 * não o catálogo inteiro. O botão traz de volta quando alguém precisa conferir
 * um item que zerou.
 */
export function SaldosPage() {
  const locations = useStockLocations();

  const [search, setSearch] = useState('');
  const [locationId, setLocationId] = useState('');
  const [belowMinimum, setBelowMinimum] = useState(false);
  const [hideZero, setHideZero] = useState(true);
  const [page, setPage] = useState(1);

  const listQuery = useQuery({
    queryKey: ['stock-balances', { search, locationId, belowMinimum, hideZero, page }],
    queryFn: () =>
      api.get<Paginated<StockBalance>>('/stock-balances', {
        search: search || undefined,
        locationId: locationId || undefined,
        belowMinimum: belowMinimum ? 'true' : undefined,
        hideZero: hideZero ? 'true' : 'false',
        page,
        perPage: 50,
      }),
  });

  const rows: BalanceRow[] = (listQuery.data?.data ?? []).map((row) => ({
    ...row,
    id: `${row.itemId}:${row.locationId}`,
  }));

  const totalCents = rows.reduce((acc, row) => acc + row.totalValueCents, 0);

  const columns: Array<Column<BalanceRow>> = [
    { key: 'sku', label: 'Código', mono: true, strong: true, render: (row) => row.sku },
    { key: 'itemName', label: 'Item', render: (row) => row.itemName },
    { key: 'locationName', label: 'Local', render: (row) => row.locationName },
    {
      key: 'quantityMilli',
      label: 'Saldo',
      right: true,
      mono: true,
      render: (row) => (
        <span style={row.belowMinimum ? { color: T.amber, fontWeight: 600 } : undefined}>
          {formatQty(row.quantityMilli)} {row.unit}
        </span>
      ),
    },
    {
      key: 'minQtyMilli',
      label: 'Mínimo',
      right: true,
      mono: true,
      render: (row) => (row.minQtyMilli > 0 ? formatQty(row.minQtyMilli) : '—'),
    },
    {
      key: 'avgCostCents',
      label: 'Custo médio',
      right: true,
      mono: true,
      render: (row) => formatCents(row.avgCostCents),
    },
    {
      key: 'totalValueCents',
      label: 'Valor',
      right: true,
      mono: true,
      strong: true,
      render: (row) => formatCents(row.totalValueCents),
    },
    {
      key: 'alert',
      label: '',
      width: 150,
      render: (row) =>
        row.belowMinimum ? <StatusBadge kind="warn">Abaixo do mínimo</StatusBadge> : null,
    },
  ];

  const hasFilters = Boolean(search || locationId || belowMinimum);

  return (
    <>
      <WorkspaceHeader
        title="Saldos de estoque"
        meta={[
          { label: 'Valor nesta página', value: formatCents(totalCents) },
        ]}
        actions={
          <Button
            icon={Download}
            disabled={rows.length === 0}
            onClick={() =>
              downloadCsv('saldos-estoque', [
                ['Código', 'Item', 'Local', 'Saldo', 'Unidade', 'Custo médio', 'Valor'],
                ...rows.map((row) => [
                  row.sku,
                  row.itemName,
                  row.locationName,
                  formatQty(row.quantityMilli),
                  row.unit,
                  formatCents(row.avgCostCents),
                  formatCents(row.totalValueCents),
                ]),
              ])
            }
          >
            Exportar CSV
          </Button>
        }
      />

      <div className="workspace__body">
        <FilterBar
          placeholder="Buscar por código ou nome do item…"
          search={search}
          onSearchChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          filters={[
            {
              key: 'location',
              label: 'Local',
              value: locationId,
              options: (locations.data ?? []).map((location) => ({
                value: location.id,
                label: `${location.code} · ${location.name}`,
              })),
              onChange: (value) => {
                setLocationId(value);
                setPage(1);
              },
            },
          ]}
        >
          <button
            type="button"
            className={`filterbar__chip${belowMinimum ? ' is-active' : ''}`}
            onClick={() => {
              setBelowMinimum((v) => !v);
              setPage(1);
            }}
          >
            <AlertTriangle size={12} aria-hidden /> Abaixo do mínimo
          </button>
          <button
            type="button"
            className={`filterbar__chip${hideZero ? '' : ' is-active'}`}
            onClick={() => {
              setHideZero((v) => !v);
              setPage(1);
            }}
          >
            {hideZero ? 'Mostrar zerados' : 'Zerados visíveis'}
          </button>
        </FilterBar>

        {listQuery.isPending && <TableSkeleton columns={8} />}
        {listQuery.isError && (
          <ErrorState message={errorMessage(listQuery.error)} onRetry={() => void listQuery.refetch()} />
        )}
        {listQuery.data && (
          <SmartDataTable
            caption="Saldos por item e local"
            columns={columns}
            rows={rows}
            meta={listQuery.data.meta}
            onPageChange={setPage}
            emptyState={
              hasFilters ? (
                <NoResultsState
                  onClear={() => {
                    setSearch('');
                    setLocationId('');
                    setBelowMinimum(false);
                  }}
                />
              ) : (
                <EmptyState
                  title="Nenhum saldo em estoque"
                  description="Registre a primeira entrada de material em Movimentações."
                />
              )
            }
          />
        )}
      </div>
    </>
  );
}
